// Заглушка на время технических работ и предупреждение о них заранее (src/lib/maintenanceWindow.ts).
// Стоит снаружи всего приложения: заглушка нужна и на экране входа, и до готовности Telegram SDK.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from './ui/Button'
import { Panel } from './ui/Tone'
import { getStartParam } from '@/lib/getStartParam'
import {
  clockSkewMs,
  formatMaintenanceEnd,
  formatMaintenanceWindow,
  isMaintenanceBypassRequested,
  maintenanceNoticeDismissKey,
  moscowDay,
  parseMaintenanceConfig,
  resolveMaintenancePhase,
  type MaintenanceConfig,
} from '@/lib/maintenanceWindow'

const BYPASS_KEY = 'maintenance_bypass'
const FETCH_INTERVAL_MS = 3 * 60 * 1000
const TICK_MS = 30 * 1000

function storageGet(storage: () => Storage, key: string): string | null {
  try { return storage().getItem(key) } catch { return null }
}
function storageSet(storage: () => Storage, key: string, value: string) {
  try { storage().setItem(key, value) } catch { /* приватный режим — живём без памяти */ }
}

// Обход запоминается на вкладку: после перезагрузки страницы проверяющий не должен снова добавлять параметр.
function readBypass(): boolean {
  if (isMaintenanceBypassRequested(window.location.search, getStartParam())) {
    storageSet(() => sessionStorage, BYPASS_KEY, '1')
    return true
  }
  return storageGet(() => sessionStorage, BYPASS_KEY) === '1'
}

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<MaintenanceConfig | null>(null)
  const [skewMs, setSkewMs] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [bypass] = useState(readBypass)
  const [dismissedDay, setDismissedDay] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/maintenance.json?t=${Date.now()}`, { cache: 'no-store' })
      if (!response.ok) return
      const parsed = parseMaintenanceConfig(await response.json())
      setSkewMs(clockSkewMs(response.headers.get('Date'), Date.now()))
      setConfig(parsed)
      if (parsed) setDismissedDay(storageGet(() => localStorage, maintenanceNoticeDismissKey(parsed)))
    } catch {
      // Сеть или сервер недоступны — остаёмся на последнем известном окне (время идёт и без файла).
    }
  }, [])

  useEffect(() => {
    void load()
    const fetchTimer = window.setInterval(() => void load(), FETCH_INTERVAL_MS)
    const tickTimer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now())
        void load()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(fetchTimer)
      window.clearInterval(tickTimer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load])

  const serverNow = now + skewMs
  const phase = resolveMaintenancePhase(config, serverNow)

  // Работы закончились — перезагрузка: сессии и запросы, упавшие в окне, начнутся заново.
  const prevPhase = useRef(phase)
  useEffect(() => {
    if (prevPhase.current === 'maintenance' && phase !== 'maintenance' && !bypass) {
      window.location.reload()
    }
    prevPhase.current = phase
  }, [phase, bypass])

  if (phase === 'maintenance' && !bypass) {
    return <MaintenanceScreen config={config} overdue={isOverdue(config, serverNow)} onRetry={() => void load()} />
  }

  const today = moscowDay(serverNow)
  const showNotice = phase === 'notice' && config !== null && dismissedDay !== today
  const dismiss = () => {
    if (!config) return
    storageSet(() => localStorage, maintenanceNoticeDismissKey(config), today)
    setDismissedDay(today)
  }

  return (
    <>
      {phase === 'maintenance' && bypass && (
        <div className="fixed bottom-2 left-2 z-[60] rounded-md bg-foreground px-2 py-1 text-xs text-background shadow">
          Технические работы: заглушка скрыта для проверки
        </div>
      )}
      {children}
      {showNotice && <MaintenanceNotice config={config} onClose={dismiss} />}
    </>
  )
}

function isOverdue(config: MaintenanceConfig | null, nowMs: number): boolean {
  const end = config?.endsAt ? Date.parse(config.endsAt) : NaN
  return !Number.isNaN(end) && nowMs >= end
}

function MaintenanceNotice({ config, onClose }: { config: MaintenanceConfig; onClose: () => void }) {
  const window_ = formatMaintenanceWindow(config)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl">
        <h3 className="mb-2 text-lg font-semibold text-foreground">Плановые технические работы</h3>
        <p className="mb-3 text-sm text-foreground">
          {window_ ? <><span className="font-medium">{window_}</span> по московскому времени</> : 'В ближайшее время'}{' '}
          приложение будет недоступно.
        </p>
        <Panel tone="warn" size="lg" className="mb-4">
          {config.message || 'Пожалуйста, внесите данные заранее: во время работ сохранить их не получится. Всё, что внесено до начала работ, сохранится.'}
        </Panel>
        <Button className="w-full" onClick={onClose}>Понятно</Button>
      </div>
    </div>
  )
}

function MaintenanceScreen({ config, overdue, onRetry }: { config: MaintenanceConfig | null; overdue: boolean; onRetry: () => void }) {
  const end = formatMaintenanceEnd(config)
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-4 text-center">
        <div className="text-5xl" aria-hidden="true">🛠️</div>
        <h1 className="text-xl font-semibold text-foreground">Ведутся технические работы</h1>
        <p className="text-sm text-muted-foreground">
          {overdue || !end
            ? 'Работы занимают чуть больше времени, чем планировали. Приложение скоро снова заработает.'
            : <>Приложение временно недоступно и снова заработает ориентировочно в <span className="font-medium text-foreground">{end}</span> по московскому времени.</>}
        </p>
        <p className="text-sm text-muted-foreground">Данные, внесённые до начала работ, сохранены.</p>
        <Button variant="outline" onClick={onRetry}>Проверить снова</Button>
      </div>
    </div>
  )
}
